import React, { useState, useEffect, useRef } from 'react';
import {
    MapPin, Bell, Battery, Eye,
    Shield, CheckCircle, X, ChevronRight,
    Loader2, AlertCircle,
} from 'lucide-react';
import { PermissionsService } from '../services/PermissionsService';
import { Capacitor } from '@capacitor/core';

interface PermissionsModalProps {
    userId?: string;
    onComplete: () => void;
}

type StepStatus = 'pending' | 'requesting' | 'granted' | 'denied' | 'verifying';

const STEPS = [
    {
        id: 'gps' as const,
        icon: MapPin,
        colorBg: 'bg-sky-50',
        colorRing: 'bg-sky-100',
        colorIcon: 'text-sky-600',
        colorBorder: 'border-sky-200',
        colorBtn: 'bg-sky-600 hover:bg-sky-700',
        colorBar: 'bg-sky-400',
        title: 'Ubicación GPS',
        label: 'GPS',
        purpose: [
            'Encontrar veterinarias y albergues cercanos a ti',
            'Rastrear a tu mascota en el mapa en tiempo real',
            'Recibir alertas SOS de mascotas perdidas en tu zona',
        ],
        btnLabel: 'Conceder permiso de GPS',
        type: 'native' as const,
        consequence: 'no podrás ver el mapa, veterinarias cercanas ni el rastreo de tu mascota.',
    },
    {
        id: 'notifications' as const,
        icon: Bell,
        colorBg: 'bg-violet-50',
        colorRing: 'bg-violet-100',
        colorIcon: 'text-violet-600',
        colorBorder: 'border-violet-200',
        colorBtn: 'bg-violet-600 hover:bg-violet-700',
        colorBar: 'bg-violet-400',
        title: 'Alertas y Notificaciones',
        label: 'Alertas',
        purpose: [
            'Recordatorios médicos y de vacunas de tu mascota',
            'Alertas de emergencia SOS en tiempo real 24/7',
            'Avisos de mascotas perdidas cerca de tu ubicación',
        ],
        btnLabel: 'Conceder permiso de Alertas',
        type: 'native' as const,
        consequence: 'NO recibirás recordatorios de dosis ni alertas SOS de mascotas perdidas.',
    },
    {
        id: 'battery' as const,
        icon: Battery,
        colorBg: 'bg-amber-50',
        colorRing: 'bg-amber-100',
        colorIcon: 'text-amber-600',
        colorBorder: 'border-amber-200',
        colorBtn: 'bg-amber-500 hover:bg-amber-600',
        colorBar: 'bg-amber-400',
        title: 'Sin restricción de batería',
        label: 'Batería',
        purpose: [
            'GPS activo aunque el teléfono esté en reposo',
            'Alarmas SOS que suenan incluso con batería baja',
            'Rastreo continuo sin interrupciones del sistema Android',
        ],
        btnLabel: 'Activar sin restricciones',
        type: 'system_dialog' as const,
        instruction: 'Aparecerá un diálogo del sistema. Toca "Permitir" o "Sin restricciones" para confirmar.',
        consequence: 'el teléfono puede "dormir" la app y saltarse alarmas con la pantalla apagada.',
    },
    {
        id: 'overlay' as const,
        icon: Eye,
        colorBg: 'bg-emerald-50',
        colorRing: 'bg-emerald-100',
        colorIcon: 'text-emerald-600',
        colorBorder: 'border-emerald-200',
        colorBtn: 'bg-emerald-600 hover:bg-emerald-700',
        colorBar: 'bg-emerald-400',
        title: 'Mostrar alertas SOS',
        label: 'Alertas SOS',
        purpose: [
            'Mostrar alertas SOS críticas sobre cualquier app',
            'Alarmas de emergencia visibles aunque el teléfono esté bloqueado',
            'Notificaciones urgentes que no pasan desapercibidas',
        ],
        btnLabel: 'Habilitar alertas SOS',
        type: 'system_dialog' as const,
        instruction: 'En la pantalla de ajustes, activa el interruptor "Permitir mostrar sobre otras apps" para Amigo Peludo.',
        consequence: 'las alertas SOS y de dosis pueden no abrirse cuando estés usando otra app o con el teléfono bloqueado.',
    },
] as const;

type StepId = typeof STEPS[number]['id'];

// Estado REAL de cada paso (no lo que la app "cree" que pasó al volver de ajustes).
const checkStepGranted = async (id: StepId): Promise<boolean> => {
    if (id === 'gps' || id === 'notifications') {
        const p = await PermissionsService.checkPermissions();
        if (id === 'gps') return p.location === 'granted';
        // Además del permiso, que las notificaciones no estén apagadas a nivel teléfono.
        const full = await PermissionsService.getFullStatus();
        return p.notifications === 'granted' && (full ? full.notifications : true);
    }
    if (id === 'battery') return PermissionsService.checkBatteryOptimization();
    return PermissionsService.checkOverlayPermission();
};

export const PermissionsModal: React.FC<PermissionsModalProps> = ({ userId, onComplete }) => {
    const [phase, setPhase] = useState<'checking' | 'intro' | 'step' | 'done'>('checking');
    const [currentStep, setCurrentStep] = useState(0);
    const [stepStatus, setStepStatus] = useState<Record<StepId, StepStatus>>({
        gps: 'pending',
        notifications: 'pending',
        battery: 'pending',
        overlay: 'pending',
    });

    // Refs para el visibilitychange handler (evita closures obsoletos)
    const currentStepRef = useRef(currentStep);
    const stepStatusRef = useRef(stepStatus);
    const phaseRef = useRef(phase);
    useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
    useEffect(() => { stepStatusRef.current = stepStatus; }, [stepStatus]);
    useEffect(() => { phaseRef.current = phase; }, [phase]);

    // Inicialización: verificar estados reales con APIs nativas
    useEffect(() => {
        const init = async () => {
            if (!Capacitor.isNativePlatform()) { onComplete(); return; }

            const perms = await PermissionsService.checkPermissions();
            const batteryOk = await PermissionsService.checkBatteryOptimization();
            const overlayOk = await PermissionsService.checkOverlayPermission();

            const next: Record<StepId, StepStatus> = {
                gps: perms.location === 'granted' ? 'granted' : 'pending',
                notifications: perms.notifications === 'granted' ? 'granted' : 'pending',
                battery: batteryOk ? 'granted' : 'pending',
                overlay: overlayOk ? 'granted' : 'pending',
            };
            setStepStatus(next);

            if (Object.values(next).every(s => s === 'granted')) { onComplete(); return; }
            setPhase('intro');
        };
        init();
    }, []);

    // Auto-avanzar cuando un paso nativo queda 'granted'
    useEffect(() => {
        if (phase !== 'step') return;
        const step = STEPS[currentStep];
        if (!step || stepStatus[step.id] !== 'granted') return;

        const t = setTimeout(() => {
            const next = currentStep + 1;
            if (next < STEPS.length) {
                setCurrentStep(next);
            } else {
                setPhase('done');
                setTimeout(onComplete, 1600);
            }
        }, 1100);
        return () => clearTimeout(t);
    }, [phase, currentStep, stepStatus]);

    // Verificación REAL al regresar del diálogo del sistema (usa refs, sin closures obsoletos)
    useEffect(() => {
        const onVisible = async () => {
            if (document.visibilityState !== 'visible') return;
            if (phaseRef.current !== 'step') return;

            const step = STEPS[currentStepRef.current];
            if (!step) return;
            if (stepStatusRef.current[step.id] !== 'verifying') return;

            // Consultar la API nativa — NO asumir, verificar de verdad
            const granted = await checkStepGranted(step.id);

            setStepStatus(prev => ({
                ...prev,
                [step.id]: granted ? 'granted' : 'denied',
            }));
        };

        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []); // deps vacíos a propósito — todo el estado viene de refs

    const handleRequest = async () => {
        const step = STEPS[currentStep];
        setStepStatus(prev => ({ ...prev, [step.id]: 'requesting' }));

        if (step.type === 'native') {
            let granted = false;
            if (step.id === 'gps') {
                granted = await PermissionsService.requestLocationPermission();
                if (granted && userId) PermissionsService.updateUserLocationInBackground(userId);
            } else if (step.id === 'notifications') {
                granted = await PermissionsService.requestNotificationPermission();
            }
            setStepStatus(prev => ({ ...prev, [step.id]: granted ? 'granted' : 'denied' }));
        } else {
            // system_dialog: marcar como 'verifying' y abrir el diálogo del sistema
            setStepStatus(prev => ({ ...prev, [step.id]: 'verifying' }));
            if (step.id === 'battery') {
                await PermissionsService.requestBatteryOptimizationPermission();
            } else if (step.id === 'overlay') {
                await PermissionsService.requestOverlayPermissionIntent();
            }
            // La verificación real ocurre en el listener visibilitychange
        }
    };

    // Re-verificar manualmente (botón "Verificar ahora")
    const handleReverify = async () => {
        const step = STEPS[currentStep];
        setStepStatus(prev => ({ ...prev, [step.id]: 'requesting' }));
        const granted = await checkStepGranted(step.id);
        setStepStatus(prev => ({ ...prev, [step.id]: granted ? 'granted' : 'denied' }));
    };

    // Reintento tras un rechazo: un permiso de diálogo normal (GPS/notificaciones)
    // denegado dos veces ya no vuelve a mostrar el diálogo — hay que llevar al
    // usuario a los ajustes de la app en vez de repetir una petición muda.
    const handleRetry = async () => {
        const step = STEPS[currentStep];
        if (step.type === 'native') {
            setStepStatus(prev => ({ ...prev, [step.id]: 'verifying' }));
            if (step.id === 'notifications') await PermissionsService.fixNotifications();
            else await PermissionsService.fixLocation();
            // fixLocation/fixNotifications piden el diálogo si todavía se puede: comprobar ya.
            const granted = await checkStepGranted(step.id);
            if (granted) setStepStatus(prev => ({ ...prev, [step.id]: 'granted' }));
        } else {
            await handleRequest();
        }
    };

    // Salir sin activar es posible (algunos teléfonos no tienen esa pantalla de
    // ajustes), pero solo tras un intento fallido y avisando qué se pierde.
    const handleContinueWithout = () => {
        const step = STEPS[currentStep];
        const ok = window.confirm(`Sin "${step.title}" ${step.consequence}\n\n¿Continuar de todas formas? Puedes activarlo después en Mi perfil → Estado de permisos.`);
        if (ok) advance();
    };

    const advance = () => {
        const next = currentStep + 1;
        if (next < STEPS.length) {
            setCurrentStep(next);
        } else {
            setPhase('done');
            setTimeout(onComplete, 1600);
        }
    };

    const step = STEPS[currentStep];
    const status = step ? stepStatus[step.id] : 'pending';
    const isGranted = status === 'granted';
    const isRequesting = status === 'requesting';
    const isDenied = status === 'denied';
    const isVerifying = status === 'verifying';

    if (phase === 'checking') return null;

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col bg-white select-none">

            {/* ── INTRO ────────────────────────────────────────────────── */}
            {phase === 'intro' && (
                <>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 px-6 pt-14 pb-8 text-white shrink-0">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                            <Shield size={30} className="text-white" />
                        </div>
                        <h1 className="text-2xl font-black mb-1 leading-tight">Permisos necesarios</h1>
                        <p className="text-orange-100 text-sm">
                            Para proteger a tu mascota necesitamos 4 autorizaciones
                        </p>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
                        <p className="text-slate-500 text-sm mb-5 leading-relaxed">
                            Otorgaremos cada permiso de forma individual. Podrás ver exactamente para qué se usará antes de aceptar.
                        </p>

                        <div className="space-y-3">
                            {STEPS.map((s, i) => {
                                const st = stepStatus[s.id];
                                return (
                                    <div key={s.id}
                                        className={`flex items-center gap-4 p-4 rounded-2xl border ${s.colorBorder} ${s.colorBg}`}>
                                        <div className={`w-10 h-10 rounded-full ${s.colorRing} flex items-center justify-center shrink-0`}>
                                            {st === 'granted'
                                                ? <CheckCircle size={20} className="text-green-500" />
                                                : <span className={`text-sm font-black ${s.colorIcon}`}>{i + 1}</span>
                                            }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-black text-slate-900 text-sm">{s.title}</p>
                                            <p className="text-xs text-slate-500">{s.label}</p>
                                        </div>
                                        {st === 'granted'
                                            ? <CheckCircle size={16} className="text-green-500 shrink-0" />
                                            : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="px-6 pb-10 pt-3 shrink-0">
                        <button
                            onClick={() => {
                                const first = STEPS.findIndex(s => stepStatus[s.id] !== 'granted');
                                setCurrentStep(first === -1 ? 0 : first);
                                setPhase('step');
                            }}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-black py-4 rounded-2xl text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange-500/25">
                            Comenzar configuración <ChevronRight size={16} />
                        </button>
                    </div>
                </>
            )}

            {/* ── STEP ─────────────────────────────────────────────────── */}
            {phase === 'step' && step && (
                <>
                    <div className="h-1.5 bg-slate-100 shrink-0">
                        <div
                            className="h-full bg-orange-500 transition-all duration-500"
                            style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-5">
                            Paso {currentStep + 1} de {STEPS.length}
                        </p>

                        {/* Dots de progreso con estado real por permiso */}
                        <div className="flex gap-2 mb-8">
                            {STEPS.map((s, i) => {
                                const st = stepStatus[s.id];
                                return (
                                    <div key={s.id}
                                        className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                                            st === 'granted' ? 'bg-green-400' :
                                            st === 'denied' ? 'bg-red-300' :
                                            i === currentStep ? s.colorBar : 'bg-slate-200'
                                        }`}
                                    />
                                );
                            })}
                        </div>

                        <div className={`w-20 h-20 ${step.colorBg} border ${step.colorBorder} rounded-3xl flex items-center justify-center mb-5`}>
                            <step.icon size={36} className={step.colorIcon} />
                        </div>

                        <h2 className="text-2xl font-black text-slate-900 mb-1 leading-tight">
                            {step.title}
                        </h2>
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-5">
                            Permiso {currentStep + 1} / {STEPS.length}
                        </p>

                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-3">
                            Se usará para:
                        </p>
                        <ul className="space-y-2.5 mb-6">
                            {step.purpose.map((p, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <div className={`w-5 h-5 rounded-full ${step.colorRing} flex items-center justify-center shrink-0 mt-0.5`}>
                                        <span className={`text-[9px] font-black ${step.colorIcon}`}>✓</span>
                                    </div>
                                    <span className="text-sm text-slate-600 leading-relaxed">{p}</span>
                                </li>
                            ))}
                        </ul>

                        {'instruction' in step && step.instruction && !isGranted && (
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-start gap-3">
                                <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800 leading-relaxed">
                                    <span className="font-black">Instrucción: </span>
                                    {step.instruction}
                                </p>
                            </div>
                        )}

                        {isGranted && (
                            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4">
                                <CheckCircle size={22} className="text-green-500 shrink-0" />
                                <div>
                                    <p className="font-black text-green-700 text-sm">¡Permiso concedido!</p>
                                    <p className="text-xs text-green-600">Pasando al siguiente automáticamente…</p>
                                </div>
                            </div>
                        )}

                        {isDenied && (
                            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
                                <X size={22} className="text-red-500 shrink-0" />
                                <div>
                                    <p className="font-black text-red-700 text-sm">No activado</p>
                                    <p className="text-xs text-red-600">
                                        {step.type === 'system_dialog'
                                            ? 'Puedes intentarlo de nuevo o continuar sin este permiso.'
                                            : 'Puedes activarlo después desde los Ajustes del sistema.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {isVerifying && (
                            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
                                <Loader2 size={22} className="text-blue-500 shrink-0 animate-spin" />
                                <div>
                                    <p className="font-black text-blue-700 text-sm">Esperando que regreses…</p>
                                    <p className="text-xs text-blue-600">Actívalo en los ajustes y vuelve aquí</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-6 pb-10 pt-2 space-y-3 shrink-0">
                        {!isGranted && !isVerifying && !isDenied && (
                            <button
                                onClick={handleRequest}
                                disabled={isRequesting}
                                className={`w-full ${step.colorBtn} text-white font-black py-4 rounded-2xl text-sm flex items-center justify-center gap-2 transition-all shadow-lg disabled:opacity-60`}>
                                {isRequesting
                                    ? <><Loader2 size={16} className="animate-spin" /> Solicitando…</>
                                    : <>{step.btnLabel} <ChevronRight size={16} /></>
                                }
                            </button>
                        )}

                        {/* Botón "Verificar ahora" — permite comprobar sin esperar el visibilitychange */}
                        {isVerifying && (
                            <button
                                onClick={handleReverify}
                                className={`w-full ${step.colorBtn} text-white font-black py-4 rounded-2xl text-sm flex items-center justify-center gap-2 transition-all`}>
                                Ya lo activé — Verificar <CheckCircle size={16} />
                            </button>
                        )}

                        {/* Solo se avanza con un permiso ya activo. Antes había un
                            "Siguiente permiso"/"Saltar este paso" que dejaba pasar sin
                            activar nada — y así se quedaban sin alarmas sin saberlo. */}
                        {isGranted && (
                            <button
                                onClick={advance}
                                className="w-full bg-slate-800 hover:bg-slate-900 text-white font-black py-4 rounded-2xl text-sm flex items-center justify-center gap-2 transition-all">
                                {currentStep < STEPS.length - 1 ? 'Siguiente permiso' : 'Finalizar'}
                                <ChevronRight size={16} />
                            </button>
                        )}

                        {isDenied && (
                            <>
                                <button
                                    onClick={handleRetry}
                                    className={`w-full ${step.colorBtn} text-white font-black py-4 rounded-2xl text-sm flex items-center justify-center gap-2 transition-all shadow-lg`}>
                                    {step.type === 'native' ? 'Abrir ajustes y activarlo' : 'Intentar de nuevo'} <ChevronRight size={16} />
                                </button>
                                <button
                                    onClick={handleContinueWithout}
                                    className="w-full text-slate-400 text-xs font-bold py-2 text-center underline underline-offset-2">
                                    Continuar sin activarlo
                                </button>
                            </>
                        )}
                    </div>
                </>
            )}

            {/* ── DONE ─────────────────────────────────────────────────── */}
            {phase === 'done' && (
                <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle size={48} className="text-green-500" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 mb-2">¡Todo listo!</h2>
                    <p className="text-slate-500 text-sm max-w-xs leading-relaxed">
                        La app está configurada correctamente. Ahora puedes disfrutar de todas las funciones de Amigo Peludo.
                    </p>
                    <div className="flex gap-3 mt-8">
                        {STEPS.map(s => {
                            const st = stepStatus[s.id];
                            return (
                                <div key={s.id}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        st === 'granted' ? 'bg-green-100' : 'bg-slate-100'
                                    }`}>
                                    {st === 'granted'
                                        ? <CheckCircle size={16} className="text-green-500" />
                                        : <X size={14} className="text-slate-400" />
                                    }
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex gap-3 mt-2">
                        {STEPS.map(s => (
                            <p key={s.id} className="text-[9px] text-slate-400 font-bold" style={{ width: '40px' }}>
                                {s.label}
                            </p>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
